'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AlertCircle, GitBranch, GitPullRequest, Shield } from 'lucide-react'

interface PushDiscoveryModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: { branch: string; createPR: boolean; commitMessage: string }) => Promise<void>
  repositoryName: string
}

export function PushDiscoveryModal({
  open,
  onClose,
  onSubmit,
  repositoryName,
}: PushDiscoveryModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [branch, setBranch] = useState('etherpilot-discovery')
  const [createPR, setCreatePR] = useState(true)
  const [commitMessage, setCommitMessage] = useState('Add EtherPilot network and invariants')

  const handleSubmit = async () => {
    if (!branch.trim()) {
      setError('Branch name is required')
      return
    }

    setLoading(true)
    setError(null)
    try {
      await onSubmit({ branch, createPR, commitMessage })
      onClose()
      // Reset form
      setBranch('etherpilot-discovery')
      setCreatePR(true)
      setCommitMessage('Add EtherPilot network and invariants')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to push to GitHub')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Push to GitHub</DialogTitle>
          <DialogDescription>
            Push generated network and invariants files to {repositoryName}
          </DialogDescription>
        </DialogHeader>

        <div className="py-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="branch">
              <GitBranch className="inline h-4 w-4 mr-1" />
              Branch Name
            </Label>
            <Input
              id="branch"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              placeholder="etherpilot-discovery"
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              The branch where files will be committed. If it doesn't exist, it will be created.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="commitMessage">Commit Message</Label>
            <Input
              id="commitMessage"
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              placeholder="Add EtherPilot network and invariants"
              disabled={loading}
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="createPR"
              checked={createPR}
              onChange={(e) => setCreatePR(e.target.checked)}
              disabled={loading}
              className="h-4 w-4"
            />
            <Label
              htmlFor="createPR"
              className="text-sm font-normal cursor-pointer flex items-center"
            >
              <GitPullRequest className="h-4 w-4 mr-1" />
              Create Pull Request
            </Label>
          </div>
          <p className="text-xs text-muted-foreground ml-6">
            {createPR
              ? 'A pull request will be created to merge the changes into your main branch'
              : 'Files will be committed directly to the specified branch'}
          </p>

          <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg space-y-2 border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <Shield className="h-4 w-4 mt-0.5 text-blue-600 dark:text-blue-400" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">GitHub App Permissions Required</p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Ensure the EtherPilot GitHub App has <strong>Contents: Read and write</strong> permission
                  and <strong>Pull requests: Read and write</strong> permission for this repository.
                </p>
                <a
                  href={`https://github.com/${repositoryName}/settings/installations`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline inline-block"
                >
                  Configure permissions →
                </a>
              </div>
            </div>
          </div>

          <div className="bg-muted p-4 rounded-lg space-y-2">
            <p className="text-sm font-medium">Files to be pushed:</p>
            <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
              <li>.etherpilot/network.yml</li>
              <li>.etherpilot/invariants.yml</li>
            </ul>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950 p-3 rounded-lg flex items-start gap-2 border border-red-200 dark:border-red-800">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="font-medium">{error}</p>
                {error.includes('write access') || error.includes('permission') ? (
                  <p className="text-xs text-red-500">
                    The GitHub App may not have write permissions. Please check the repository settings.
                  </p>
                ) : null}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Pushing...' : 'Push to GitHub'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}